import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Save, Calculator, Camera, Mic, FileText, HardDrive, Send } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { useCostConfigs, useUpdateCostConfig } from '@/hooks/useCosts';

const getCostIcon = (type: string) => {
  switch (type) {
    case 'photo':
      return <Camera className="w-5 h-5" />;
    case 'audio':
      return <Mic className="w-5 h-5" />;
    case 'text':
      return <FileText className="w-5 h-5" />;
    case 'storage':
      return <HardDrive className="w-5 h-5" />;
    case 'whatsapp':
      return <Send className="w-5 h-5" />;
    default:
      return <DollarSign className="w-5 h-5" />;
  }
};

const getCostLabel = (type: string) => {
  switch (type) {
    case 'photo':
      return 'Foto (OCR/Vision)';
    case 'audio':
      return 'Audio (Whisper)';
    case 'text':
      return 'Texto (AI Parse)';
    case 'storage':
      return 'Storage (por MB)';
    case 'whatsapp':
      return 'WhatsApp (por msg)';
    default:
      return type;
  }
};

export const CostsConfigPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: configs, isLoading } = useCostConfigs();
  const updateMutation = useUpdateCostConfig();

  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const handleValueChange = (type: string, value: string) => {
    setEditedValues(prev => ({ ...prev, [type]: value }));
  };

  const handleSave = async (type: string) => {
    const value = editedValues[type];
    if (!value) return;

    setSaving(type);
    try {
      await updateMutation.mutateAsync({ type, costPerUnit: parseFloat(value) });
      setEditedValues(prev => {
        const updated = { ...prev };
        delete updated[type];
        return updated;
      });
    } finally {
      setSaving(null);
    }
  };

  const hasChanges = (type: string, currentValue: number) => {
    const edited = editedValues[type];
    if (!edited) return false;
    return parseFloat(edited) !== currentValue;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configuracao de Custos</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Configure os custos por tipo de operacao
          </p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<Calculator className="w-4 h-4" />}
          onClick={() => navigate('/admin/costs/margin')}
        >
          Analise de Margem
        </Button>
      </div>

      {/* Cost Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {configs?.map((config) => (
          <Card key={config.type}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                {getCostIcon(config.type)}
                {getCostLabel(config.type)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Input
                    label="Custo por Unidade (USD)"
                    type="number"
                    step="0.0001"
                    value={editedValues[config.type] ?? config.costPerUnit.toString()}
                    onChange={(e) => handleValueChange(config.type, e.target.value)}
                    leftIcon={<DollarSign className="w-4 h-4" />}
                  />
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Ultima atualizacao:</span>
                  <span>{new Date(config.updatedAt).toLocaleDateString('pt-BR')}</span>
                </div>

                {hasChanges(config.type, config.costPerUnit) && (
                  <Button
                    className="w-full"
                    leftIcon={<Save className="w-4 h-4" />}
                    onClick={() => handleSave(config.type)}
                    isLoading={saving === config.type}
                  >
                    Salvar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                Como funcionam os custos
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Os custos sao usados para calcular a margem de lucro por usuario e por plano.
                Eles representam o custo real de cada operacao (APIs de AI, storage, WhatsApp, etc).
                Atualize regularmente para manter a analise de margem precisa.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CostsConfigPage;
